<template>
  <default>
    <div class="text-center col-md-10 ms-auto me-auto">
      <!-- Loading -->
      <div v-if="loading" class="my-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Načítání...</span>
        </div>
      </div>

      <!-- Error / 404 -->
      <div v-else-if="error" class="alert alert-warning mt-4">
        <p class="mb-3">{{ error }}</p>
        <router-link :to="{ name: 'order' }" class="btn btn-success">
          <i class="bi bi-arrow-left me-2"></i>Zpět na objednávku
        </router-link>
      </div>

      <!-- Order loaded -->
      <template v-else-if="order">
        <div class="alert alert-success mt-4 py-4">
          <i class="bi bi-check-circle-fill fs-1 d-block mb-2"></i>
          <p class="h3 mb-1">Objednávka byla přijata do systému.</p>
          <p v-if="order.custom?.orderType === 'supplement'" class="mb-0 text-muted">
            Dodatečná objednávka č. {{ order.counterNumber }} k objednávce č. {{ order.custom.parentOrderNumber }} byla zaevidována.
          </p>
          <p v-else class="mb-0 text-muted">Objednávka č. {{ order.counterNumber }} byla zaevidována.</p>
        </div>

        <p class="text-muted my-3">
          Na sklad bylo odesláno potvrzení objednávky. Zboží bude doručeno v požadovaný termín.
          Přehled všech svých objednávek najdete v sekci <strong>Přehled objednávek</strong>.
        </p>

        <div class="card text-start mb-4">
          <div class="card-header fw-semibold">Shrnutí objednávky</div>
          <div class="card-body">

            <div v-if="order.custom" class="mb-3">
              <p v-if="order.custom.note" class="mb-1"><strong>Poznámka:</strong> {{ order.custom.note }}</p>
              <p v-if="order.custom.deliveryDate" class="mb-0"><strong>Datum doručení:</strong> {{ formatDate(order.custom.deliveryDate) }}</p>
            </div>

            <h6 class="mt-2">Objednané položky</h6>
            <table class="table table-order mb-0">
              <thead class="table-light">
              <tr>
                <th>Název</th>
                <th>Množství</th>
                <th>Jednotka</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in orderedItems" :key="'ordered-' + index">
                <td>{{ item.name }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.custom?.orderUnit }}</td>
              </tr>
              </tbody>
            </table>

            <div v-if="reclaimedItems.length" class="mt-3">
              <h6>Reklamované položky</h6>
              <table class="table table-order">
                <thead>
                <tr>
                  <th>Název</th>
                  <th>Množství</th>
                  <th>Jednotka</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, index) in reclaimedItems" :key="'reclaimed-' + index">
                  <td>{{ item.name }}</td>
                  <td>{{ item.quantity }}</td>
                  <td>{{ item.unit }}</td>
                </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>

        <router-link :to="{ name: 'order' }" class="btn btn-success btn-lg mt-2 mb-4">
          <i class="bi bi-plus-circle me-2"></i>Zadat další objednávku
        </router-link>

        <div class="alert alert-warning border-warning mt-4 mb-4 py-4 shadow-sm">
          <i class="bi bi-megaphone-fill fs-1 d-block mb-2"></i>
          <h4 class="mb-3 fw-bold">Dejte nám vědět, jak se vám aplikace používá!</h4>
          <p class="mb-3">
            Pokud vám něco chybí, zlobí, nebo byste rádi cokoliv vylepšili — napište nám.
            Vaše zpětná vazba nám pomáhá dělat aplikaci lepší.
          </p>
          <button type="button" class="btn btn-dark btn-lg fw-semibold" @click="openFeedback">
            <i class="bi bi-chat-dots me-2"></i>Napište nám
          </button>
        </div>
      </template>
    </div>
  </default>
</template>

<script lang="ts">
import {useAppStore} from "@/stores/appStore";
import * as Sentry from "@sentry/vue";
import Default from "@/layouts/default.vue";
import type {ResponseEntityOrder} from "@haxo-cz/orders-ts-client";

export default {
  components: {Default},
  data() {
    return {
      appStore: useAppStore(),
      order: null as null | ResponseEntityOrder,
      loading: true,
      error: "",
    };
  },
  methods: {
    formatDate(dateStr: string | null | undefined) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    async openFeedback() {
      const feedback = Sentry.getFeedback();
      if (!feedback) return;
      const form = await feedback.createForm();
      form.appendToDom();
      form.open();
    },
    async fetchOrder() {
      if (!this.appStore.selectedProject) {
        this.error = "Není vybraný projekt.";
        this.loading = false;
        return;
      }
      try {
        const response = await this.$ordersApi.findOrder(
          this.appStore.selectedProject.spaceId,
          this.appStore.selectedProject._id,
          this.$route.params.orderId as string,
        );
        this.order = response.data;
      } catch (e: any) {
        if (e?.response?.status === 404) {
          this.error = "Tato objednávka neexistuje nebo k ní nemáte přístup.";
        } else {
          this.error = "Nepodařilo se načíst objednávku.";
          console.error("Chyba při načítání objednávky:", e);
        }
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    orderedItems() {
      return this.order?.items?.filter(item => !item.custom?.reclamation) ?? [];
    },
    reclaimedItems() {
      return this.order?.items?.filter(item => item.custom?.reclamation) ?? [];
    },
  },
  async mounted() {
    window.scrollTo({top: 0, behavior: 'smooth'});
    await this.fetchOrder();
  },
};
</script>
